import {partition} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import {
  ANTIBODY_DOMAIN_CORNER_RADIUS,
  ANTIBODY_DOMAIN_HEIGHT,
  ANTIBODY_DOMAIN_WIDTH,
  TerminusStyle,
} from 'antibodies/AntibodyConstants';
import {
  AntibodyFormat,
  AntibodyFormatChainDomain,
  AntibodyFormatDomain,
  AntibodyFormatHingeDomain,
  AntibodyFormatLinkerDomain,
  ChainDomainSizingParams,
  DomainDisplayParams,
  SizingParams,
} from 'antibodies/AntibodyTypedefs';
import {domainColor} from 'antibodies/AntibodyUtils';
import AntibodyDomain from 'antibodies/visualization/AntibodyDomain';
import AntibodyDomainLabel from 'antibodies/visualization/AntibodyDomainLabel';
import AntibodyHinge from 'antibodies/visualization/AntibodyHinge';
import AntibodyLinker from 'antibodies/visualization/AntibodyLinker';
import InteractiveDomainWrapper, {
  AntibodyInteractivityProps,
} from 'antibodies/visualization/InteractiveDomainWrapper';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';
import sortedBy from 'util/sortedBy';

function assertNoJoiners(
  domains: Array<AntibodyFormatDomain>
): asserts domains is Array<
  AntibodyFormatChainDomain | AntibodyFormatHingeDomain | AntibodyFormatLinkerDomain
> {
  assert(
    domains.every(({type}) => type !== AntibodyDomainType.J),
    "we don't know how to make joiners yet"
  );
}

/**
 * Given transform params, get the actual transform string that should be given to the SVG `g` wrapper for
 * this domain.
 * @param translate How far to move the domain, both horizontally and vertically
 * @param rotate How many degrees clockwise to rotate the domain
 * @param reflect Whether to reflect the domain horizontally
 */
function getDomainTransform({translate, rotate, reflect}: DomainDisplayParams): string {
  const translateX = translate?.x ?? 0;
  const translateY = translate?.y ?? 0;
  const translation = `translate(${translate?.x ?? 0}, ${translate?.y ?? 0})`;
  // Rotate around the top left corner of the bounding box of the domain, not around the origin of the SVG.
  const rotation = rotate != null ? `rotate(${rotate} ${translateX} ${translateY})` : '';
  const reflection = reflect ? 'scale(-1, 1)' : '';
  return [rotation, translation, reflection].join(' ');
}

/**
 * Given transform params, get the actual transform string that should be given to the SVG `g` wrapper for
 * this domain label.
 *
 * Footgun alert: This function assumes that this transform is being applied to an element with SVG styles
 * of transform-box: fill-box and transform-origin: center. This is because SVG does not alter the transformed
 * element's own coordinate system, only that of the transformed element's children. There is no easy way to
 * rotate an element around its own center in SVG, so we do it with CSS instead.
 *
 * @param rotate How many degrees clockwise the domain was rotated. This rotation will be reversed for the
 *               label.
 * @param height How tall the domain is
 * @param width How wide the domain is
 */
function getLabelTransform(
  {rotate}: DomainDisplayParams,
  {height = ANTIBODY_DOMAIN_HEIGHT, width = ANTIBODY_DOMAIN_WIDTH}: ChainDomainSizingParams
): string {
  const labelHeight = height / 4;
  const labelWidth = (3 * width) / 4;
  const translateX = width / 2 - labelWidth / 2;
  const translateY = height / 2 - labelHeight / 2;
  const translation = `translate(${translateX}, ${translateY})`;
  // Rotate the _opposite_ direction from the domain. We always want to render this text horizontally.
  const rotation = rotate != null ? `rotate(${-rotate})` : '';
  return [translation, rotation].join(' ');
}

export interface AntibodyProps {
  displayByRef: Record<number, DomainDisplayParams>;
  format: AntibodyFormat;
  interactivity?: AntibodyInteractivityProps;
  sizing: SizingParams;
  showLabels?: boolean;
}

/**
 * An SVG visualization of an antibody.
 * This is an SVG component, so all units are 'abstract user units', relative to the SVG viewbox.
 * Right now, all of these props are taken from one of six hard-coded objects in `AntibodyConstants`.
 * @param displayByRef A map from domain ref (a numerical identifier of the domain within the antibody) to a
 *                     display params object.
 * @param format The format for the antibody.
 * @param interactivity Interactivity state for the antibody. If not passed, the antibody viz will be a static
 *                      SVG.
 * @param interactivity.hoveredRef Which domain ref is currently hovered, if any.
 * @param interactivity.proteinIdByRef A map from domain ref to associated protein API ID (including inaccessible ones).
 * @param interactivity.inaccessibleProteinIdsByRef A map from domain ref to each inaccessible protein API ID.
 * @param interactivity.selectedRef Which domain ref is currently selected, if any
 * @param interactivity.setHoveredRef A function to set which domain ref is currently hovered.
 * @param interactivity.setSelectedRefs A function to set which domain ref is currently selected.
 * @param sizing Parameters for how big individual antibody components should be.
 * @param sizing.chainDomainSizing Parameters for how big chain domains should be.
 * @param sizing.connectorSizing Parameters for how big hinges, joiners, and linkers should be.
 * @param sizing.svgSizing Parameters for how big the SVG viewbox should be.
 * @param showLabels Whether to show labels on chain domains.
 */
export default function Antibody({
  displayByRef,
  format,
  interactivity,
  sizing,
  showLabels,
}: AntibodyProps): JSX.Element {
  assert(
    format.domains.every(({type}) => type !== AntibodyDomainType.J),
    "we don't know how to make joiners yet"
  );

  assertNoJoiners(format.domains);
  const [hinges, nonHinges] = partition<
    AntibodyFormatHingeDomain,
    AntibodyFormatChainDomain | AntibodyFormatLinkerDomain
  >(format.domains, ({type}) => type === AntibodyDomainType.H);
  const [linkers, chainDomains] = partition<AntibodyFormatLinkerDomain, AntibodyFormatChainDomain>(
    nonHinges,
    ({type}) => type === AntibodyDomainType.L
  );
  // Sort chain domains by selected status. SVG z-indexing is a strict function of what is rendered first. As
  // such, anything with a stroke should be rendered last.
  const sortedChainDomains = sortedBy(chainDomains, [
    ({ref}) => (interactivity?.selectedRef === ref ? 1 : -1),
  ]);

  const {chainDomainSizing, connectorSizing, svgSizing} = sizing;
  const {svgViewboxHeight, svgViewboxWidth} = svgSizing;
  const {strokeThickness} = connectorSizing;
  return (
    <>
      <svg height="100%" width="100%" viewBox={`0 0 ${svgViewboxWidth} ${svgViewboxHeight}`}>
        {hinges.map((hinge) => {
          const display = displayByRef[hinge.ref];
          assert(
            display.type === AntibodyDomainType.H,
            'Received non-hinge display params for a hinge domain'
          );
          const {stroke} = domainColor({
            distinctDomain: {type: hinge.type, specificity: hinge.specificity},
            hovered: interactivity?.hoveredRef === hinge.ref,
            selected: interactivity?.selectedRef === hinge.ref,
          });
          return (
            <g key={hinge.ref} transform={getDomainTransform(display)}>
              <InteractiveDomainWrapper domainRef={hinge.ref} interactivity={interactivity}>
                <AntibodyHinge
                  angle={display.angle}
                  color={stroke}
                  fixedEndLength={display.fixedEndLength}
                  freeEndLength={display.freeEndLength}
                  proteinId={interactivity?.proteinIdsByRef[hinge.ref] ?? null}
                  strokeThickness={strokeThickness}
                  inaccessible={interactivity?.inaccessibleProteinIdsByRef[hinge.ref] != null}
                />
              </InteractiveDomainWrapper>
            </g>
          );
        })}
        {linkers.map((linker) => {
          const display = displayByRef[linker.ref];
          assert(
            display.type === AntibodyDomainType.L,
            'Received non-linker display params for a linker domain'
          );
          const {stroke} = domainColor({
            distinctDomain: {type: linker.type, specificity: linker.specificity},
            hovered: interactivity?.hoveredRef === linker.ref,
            selected: interactivity?.selectedRef === linker.ref,
          });
          return (
            <g key={linker.ref} transform={getDomainTransform(display)}>
              <InteractiveDomainWrapper domainRef={linker.ref} interactivity={interactivity}>
                <AntibodyLinker
                  centerLength={display.centerLength}
                  color={stroke}
                  endRadius={display.endRadius}
                  proteinId={interactivity?.proteinIdsByRef[linker.ref] ?? null}
                  strokeThickness={strokeThickness}
                  tipLength={display.tipLength}
                  inaccessible={interactivity?.inaccessibleProteinIdsByRef[linker.ref] != null}
                />
              </InteractiveDomainWrapper>
            </g>
          );
        })}
        {sortedChainDomains.map((domain) => {
          const display = displayByRef[domain.ref];
          assert(
            // Need to do all three checks rather than an .includes to satisfy typescript
            display.type !== AntibodyDomainType.J &&
              display.type !== AntibodyDomainType.H &&
              display.type !== AntibodyDomainType.L,
            'Received non-chain display params for a chain domain'
          );
          const {cornerRadius, height, width} = chainDomainSizing;
          const {fill, stroke} = domainColor({
            distinctDomain: {type: domain.type, specificity: domain.specificity},
            hovered: interactivity?.hoveredRef === domain.ref,
            selected: interactivity?.selectedRef === domain.ref,
          });
          return (
            <g key={domain.ref} transform={getDomainTransform(display)}>
              <InteractiveDomainWrapper domainRef={domain.ref} interactivity={interactivity}>
                <AntibodyDomain
                  cornerRadius={cornerRadius}
                  cTerminusStyle={display.cTerminusStyle ?? TerminusStyle.FLAT}
                  height={height}
                  fillColor={fill}
                  nTerminusStyle={display.nTerminusStyle ?? TerminusStyle.FLAT}
                  proteinId={interactivity?.proteinIdsByRef[domain.ref] ?? null}
                  strokeColor={stroke}
                  width={width}
                  inaccessible={interactivity?.inaccessibleProteinIdsByRef[domain.ref] != null}
                />
                {showLabels && (
                  <g
                    transform={getLabelTransform(display, chainDomainSizing)}
                    className={css(styles.labelWrapper)}
                  >
                    <AntibodyDomainLabel
                      cornerRadius={(cornerRadius ?? ANTIBODY_DOMAIN_CORNER_RADIUS) / 2}
                      height={(height ?? ANTIBODY_DOMAIN_HEIGHT) / 4}
                      text={domain.type}
                      width={(3 * (width ?? ANTIBODY_DOMAIN_WIDTH)) / 4}
                    />
                  </g>
                )}
              </InteractiveDomainWrapper>
            </g>
          );
        })}
      </svg>
    </>
  );
}

const styles = StyleSheet.create({
  labelWrapper: {
    transformBox: 'fill-box',
    transformOrigin: 'center',
  },
});
